import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import { FAQS } from "@/data/content";

export const GleapFAQ = () => {
    const [open, setOpen] = useState(0);
    return (
        <section className="py-24 md:py-28 bg-clean" data-testid="gleap-faq">
            <div className="container-x">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-3">
                        <div className="text-slate-500 text-sm sticky top-24">Common questions</div>
                    </div>
                    <div className="md:col-span-9">
                        {FAQS.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <div
                                    key={f.q}
                                    className="faq-row"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    data-testid={`faq-row-${i}`}
                                >
                                    <div className="faq-q">
                                        <span>{f.q}</span>
                                        <motion.i
                                            className="fa-solid fa-chevron-down text-slate-500 text-xs"
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.25 }}
                                        />
                                    </div>
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                                style={{ overflow: "hidden" }}
                                            >
                                                <div className="faq-a">{f.a}</div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------
   INTEGRATIONS — reliable FA brand icons w/ authentic brand colors
------------------------------------------------------------------- */
const INT_LOGOS = [
    { name: "Salesforce",   icon: "fa-brands fa-salesforce",  color: "#00A1E0" },
    { name: "HubSpot",      icon: "fa-brands fa-hubspot",     color: "#FF7A59" },
    { name: "ServiceNow",   icon: "fa-solid fa-gears",        color: "#1F8476" },
    { name: "Zendesk",      icon: "fa-solid fa-headset",      color: "#03363D" },
    { name: "Snowflake",    icon: "fa-solid fa-snowflake",    color: "#29B5E8" },
    { name: "Stripe",       icon: "fa-brands fa-stripe",      color: "#635BFF" },
    { name: "Meta",         icon: "fa-brands fa-meta",        color: "#0866FF" },
    { name: "Atlassian",    icon: "fa-brands fa-atlassian",   color: "#0052CC" },
    { name: "WhatsApp",     icon: "fa-brands fa-whatsapp",    color: "#25D366" },
    { name: "Google Cloud", icon: "fa-brands fa-google",      color: "#4285F4" },
    { name: "Microsoft",    icon: "fa-brands fa-microsoft",   color: "#0078D4" },
    { name: "AWS",          icon: "fa-brands fa-aws",         color: "#FF9900" },
    { name: "Notion",       icon: "fa-solid fa-book",         color: "#111827" },
    { name: "Zoom",         icon: "fa-solid fa-video",        color: "#2D8CFF" },
    { name: "Jira",         icon: "fa-brands fa-jira",        color: "#0052CC" },
    { name: "GitHub",       icon: "fa-brands fa-github",      color: "#181717" },
    { name: "Freshworks",   icon: "fa-solid fa-life-ring",    color: "#25C16F" },
    { name: "Twilio",       icon: "fa-solid fa-phone",        color: "#F22F46" },
];

export const IntegrationsHub = () => {
    return (
        <section className="py-24 md:py-32 bg-cool" data-testid="integrations-hub">
            <div className="container-x">
                <Reveal>
                    <div className="text-eyebrow text-slate-500 mb-4 text-center">Integrations</div>
                </Reveal>
                <div className="max-w-3xl mx-auto text-center mb-14">
                    <Reveal delay={0.05}>
                        <h2 className="heading-section text-4xl sm:text-5xl lg:text-6xl">
                            Connect with
                            <br />
                            <span className="italic">your favourite tool.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-8 text-intro text-slate-600 text-base md:text-lg">
                            Seamlessly integrate NyraAI with your existing tech stack — CRM, ticketing,
                            telephony, data warehouses, messaging platforms and your own internal APIs.
                        </p>
                    </Reveal>
                </div>

                {/* Logo grid — FontAwesome brand icons with authentic colors */}
                <Reveal delay={0.2}>
                    <StaggerGroup className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-4">
                        {INT_LOGOS.map((l) => (
                            <StaggerChild key={l.name}>
                                <motion.div
                                    whileHover={{ y: -4, scale: 1.03 }}
                                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                    className="aspect-square rounded-2xl bg-white border border-slate-200 flex flex-col items-center justify-center gap-2 p-4 group cursor-default hover:border-slate-300 hover:shadow-md"
                                    style={{ boxShadow: "0 4px 12px -6px rgba(35,95,82,0.08)" }}
                                    data-testid={`integration-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    title={l.name}
                                >
                                    <div
                                        className="w-10 h-10 md:w-12 md:h-12 rounded-xl grid place-items-center transition-transform group-hover:scale-110"
                                        style={{
                                            background: `${l.color}12`,
                                            color: l.color,
                                        }}
                                    >
                                        <i className={`${l.icon} text-lg md:text-xl`} />
                                    </div>
                                    <div className="text-[10px] md:text-xs font-medium text-slate-700 text-center leading-tight">
                                        {l.name}
                                    </div>
                                </motion.div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </Reveal>

                <Reveal delay={0.3}>
                    <div className="mt-10 text-center text-sm text-slate-500">
                        + 40 more · <span className="text-slate-700">Open APIs & webhooks</span> for anything else in your stack
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export const FinalCTA = ({ openDemo }) => (
    <section className="py-24 md:py-32 bg-cta-glow" data-testid="final-cta">
        <div className="container-x text-center">
            <Reveal>
                <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl max-w-4xl mx-auto">
                    Instantly delight your website visitors with an <span className="italic">AI agent tailored to your brand.</span>
                </h2>
            </Reveal>

            <Reveal delay={0.15}>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                    <button onClick={openDemo} className="pill-dark" data-testid="final-cta-primary">
                        Book a demo
                    </button>
                </div>
            </Reveal>

            <Reveal delay={0.25}>
                <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500">
                    <span className="text-slate-900">★</span>
                    <span className="text-slate-900 font-semibold">4.8/5</span>
                    <span>·</span>
                    <span>50+ enterprise deployments</span>
                </div>
            </Reveal>
        </div>
    </section>
);

